import React, { useEffect } from 'react';
import './homepage.css';
import { ReactComponent as LogoSVG } from '../assets/logo.svg';
import InstagramFeed from "./InstagramFeed";

export default function HomePage() {
    useEffect(() => {
        // Close mobile nav when clicking anchor links
        const links = document.querySelectorAll('a[href^="#"]');
        const handler = (e) => {
            const targetId = e.currentTarget.getAttribute('href');
            if (targetId && targetId.startsWith('#')) {
                const navToggle = document.getElementById('nav-toggle');
                if (navToggle && navToggle.checked) navToggle.checked = false;
            }
        };
        links.forEach((a) => a.addEventListener('click', handler));
        return () => links.forEach((a) => a.removeEventListener('click', handler));
    }, []);

    // Force Google Maps directions for everyone
    const mapsLink = (address) =>
        `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;

    const MapsLink = ({ address, children }) => (
        <a
            href={mapsLink(address)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Get directions to ${address}`}
            className="address-link"
        >
            {children || address}
        </a>
    );

    return (
        <>
            <header className="header">

                <div className="logo">
                    <LogoSVG className="h-20 w-auto" />
                </div>

                <nav className="nav">
                    <input type="checkbox" id="nav-toggle" className="nav-toggle" />
                    <label htmlFor="nav-toggle" className="nav-toggle-label">
                        <span></span>
                    </label>
                    <ul className="nav-menu">
                        <li><a href="#about">About Us</a></li>
                        <li><a href="#services">Services</a></li>
                        <li><a href="#locations">Locations</a></li>
                        <li><a href="#social">Social</a></li>
                        <li><a href="#locations">Contact</a></li>
                        <li><a href="#gallery">Gallery</a></li>
                        <li><a href="#careers">Careers</a></li>
                    </ul>
                </nav>
            </header>

            <section id="home" className="hero">
                <div className="hero-content">
                    <p>
                        Your pet deserves the <b><i>best</i></b> care.<br />
                        We offer premium grooming services in Hopkins and Inver Grove Heights, Minnesota.
                    </p>
                </div>
            </section>

            <section id="about" className="about">
                <h2 className="cooper-outline">Our Beginning</h2>
                <p>
                    Zoom Groom was founded in 2010 by a 15-year-old Anna and her mom, Linda.
                    When their family faced financial challenges after Linda's cancer battle and losing their house to the housing market crash, Anna looked for a way to help her family and other families with dogs.
                    She convinced her mom to join her in grooming dogs.
                    <br/><br/>
                    With nothing but a Craigslist ad, and the most basic tools, Zoom Groom was born.
                    <br/><br/>
                    Over the years, Anna’s passion, problem-solving spirit, and commitment to pets and families has turned the scrappy little Zoom Groom into one of the most well-respected grooming salons in the United States.
                    Today, Anna is recognized as one of the top dog groomers in the United States.

                    She competes — and wins — at the largest grooming competitions in the country. Beyond the competition ring, she shares her expertise by teaching her staff and other salons, hosting seminars and webinars, and speaking at national grooming conventions, inspiring the next generation of groomers.
                </p>
            </section>

            <section id="howWeWork" className="howWeWork">
                <h2 className="cooper-outline">How We Work</h2>
                <p>
                    Zoom Groom is an 'express' grooming salon, meaning, we emphasize dropping off and picking up your pet <i><b>on time</b></i>.
                    This way of operating provides the best possible experience for your pet because:
                </p>

                <ul className="paw-list">
                    <li className="no-columns">
                        They don't have to be kenneled. Instead, they are leashed to an attachment point for
                        a short wait while the pet parent heads back to Zoom Groom.
                    </li>
                    <li>
                    Staying leashed eliminates the possibility of negative interactions with other dogs, ensuring your pets safety, without the anxiety of a cage.
                    </li>

                    <li>
                        This way of operating minimizes the pets time away from their family, minimizing stress and anxiety.
                    </li>

                    <li>
                        Your pet can go straight from the grooming table and back into your arms.
                    </li>
                </ul>

                <p>
                    When your pet's appointment is nearing completion, with about 15–20 minutes to go, we will call you
                    to give you a heads up that you can head back to pick up.
                </p>

                <p>
                    Nail services are walk-in welcome, no appointment required.<br/>
                    Grooming services are by appointment only.
                </p>

                <p>
                    Please call or email your preferred location. We may not be able to answer the phone so please leave
                    a voicemail.<br/>
                    Please allow 1 business day for us to return your call.
                </p>


            </section>

            <section id="services" className="services">
                <h2 className="cooper-outline">Services</h2>
                <div className="service-grid">
                    <div className="service-card">
                        <h3>Bath &amp; Brush</h3>
                        <p>Gentle baths, blow dry and thorough brushing to keep your dog's coat healthy and shiny.</p>
                    </div>
                    <div className="service-card">
                        <h3>Haircuts &amp; Styling</h3>
                        <p>Breed standard or customized cuts for every breed and lifestyle. Our groomers ensure your pet
                            looks their absolute best.</p>
                    </div>
                    <div className="service-card">
                        <h3>Hand Stripping</h3>
                        <p>Traditional grooming for wire-coated breeds where the blown coat is removed by hand (not
                            clippers) to preserve the harsh texture, rich color, and natural weather resistance. Ideal
                            for many terriers and some spaniels; sessions take longer than clipper cuts and are priced by size/coat condition. Recommended every 4–6 weeks.</p>
                    </div>
                    <div className="service-card">
                        <h3>Nails, Ears, Teeth, Glands</h3>
                        <p>Just like people, pets hygiene is more than just hair, fur, and skin. Luckily, we have you covered. We make sure all of your pet's needs are met.</p>
                    </div>
                </div>
            </section>

            <section id="locations" className="locations">
                <h2 className="cooper-outline">Locations</h2>
                <div className="location-grid">
                    {/* Hopkins */}
                    <div className="location-card">
                        <h3>Hopkins, MN</h3>
                        <p>
                            <MapsLink address="8 Shady Oak Rd, Hopkins, MN 55343">
                                8 Shady Oak Rd<br />Hopkins, MN 55343
                            </MapsLink>
                        </p>
                        <p>
                            <strong>Phone:</strong>{' '}
                            <a href="tel:+19529558300" aria-label="Call (952) 955-8300">(952) 955-8300</a>
                        </p>
                        <p>
                            <strong>Email:</strong>{' '}
                            <a href="mailto:zoomgroomreception@gmail.com">zoomgroomreception@gmail.com</a>
                        </p>
                        <p>
                            <strong>Social:</strong>{' '}
                            <a
                                href="https://www.instagram.com/zoomgroom_hopkins/"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Instagram - Zoom Groom Hopkins"
                            >
                                Instagram
                            </a>{' • '}
                            <a
                                href="https://www.facebook.com/zoomgroomllc"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Facebook - Zoom Groom Hopkins"
                            >
                                Facebook
                            </a>
                        </p>
                        <p><strong>Hours:</strong></p>
                        <ul>
                            <li>Sunday: 8:00&nbsp;am – 6:00&nbsp;pm</li>
                            <li>Monday: 7:00&nbsp;am – 6:00&nbsp;pm</li>
                            <li>Tuesday: 7:00&nbsp;am – 6:00&nbsp;pm</li>
                            <li>Wednesday: 7:00&nbsp;am – 6:00&nbsp;pm</li>
                            <li>Thursday: 7:00&nbsp;am – 5:00&nbsp;pm</li>
                            <li>Friday: 7:00&nbsp;am – 3:00&nbsp;pm</li>
                            <li>Saturday: Closed</li>
                        </ul>
                    </div>

                    {/* Inver Grove Heights */}
                    <div className="location-card">
                        <h3>Inver Grove Heights, MN</h3>
                        <p>
                            <MapsLink address="6555 Cahill Ave #103, Inver Grove Heights, MN 55076">
                                6555 Cahill Ave #103<br />Inver Grove Heights, MN 55076
                            </MapsLink>
                        </p>
                        <p>
                            <strong>Phone:</strong>{' '}
                            <a href="tel:+16514554551" aria-label="Call (651) 455-4551">(651) 455-4551</a>
                        </p>
                        <p>
                            <strong>Email:</strong>{' '}
                            <a href="mailto:invergrove@zoomgroomllc.com">invergrove@zoomgroomllc.com</a>
                        </p>
                        <p>
                            <strong>Social:</strong>{' '}
                            <a
                                href="https://www.instagram.com/zoomgroom_invergrove/"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Instagram - Zoom Groom Inver Grove"
                            >
                                Instagram
                            </a>{' • '}
                            <a
                                href="https://www.facebook.com/profile.php?id=61574582564781"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Facebook - Zoom Groom Inver Grove"
                            >
                                Facebook
                            </a>
                        </p>
                        <p><strong>Hours:</strong></p>
                        <ul>
                            <li>Sunday: Closed</li>
                            <li>Monday: 8:00&nbsp;am – 4:00&nbsp;pm</li>
                            <li>Tuesday: 8:00&nbsp;am – 4:00&nbsp;pm</li>
                            <li>Wednesday: 8:00&nbsp;am – 4:00&nbsp;pm</li>
                            <li>Thursday: 8:00&nbsp;am – 4:00&nbsp;pm</li>
                            <li>Friday: 8:00&nbsp;am – 4:00&nbsp;pm</li>
                            <li>Saturday: Closed</li>
                        </ul>
                    </div>
                </div>
            </section>

            <section id="social" className="social" aria-labelledby="social-heading" aria-describedby="social-desc">
                <h2 id="social-heading" className="cooper-outline">Follow Us</h2>

                <ul className="social-links">
                    <li>
                        <a
                            href="https://www.youtube.com/@zoomgroom_anna"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="YouTube - Zoom Groom Anna"
                        >
                            @zoomgroom_anna
                        </a>
                    </li>
                    <li>
                        <a
                            href="https://www.instagram.com/zoomgroom_anna/"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Instagram - Zoom Groom Anna"
                        >
                            @zoomgroom_anna
                        </a>
                    </li>
                    <li>
                        <a
                            href="https://www.instagram.com/zoomgroom_hopkins/"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Instagram - Zoom Groom Hopkins"
                        >
                            @zoomgroom_hopkins
                        </a>
                    </li>
                    <li>
                        <a
                            href="https://www.instagram.com/zoomgroom_invergrove/"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Instagram - Zoom Groom Inver Grove"
                        >
                            @zoomgroom_invergrove
                        </a>
                    </li>
                    <li>
                        <a
                            href="https://www.facebook.com/zoomgroomllc"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Facebook - Zoom Groom Hopkins"
                        >
                            Zoom Groom Hopkins
                        </a>
                    </li>
                    <li>
                        <a
                            href="https://www.facebook.com/profile.php?id=61574582564781"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Facebook - Zoom Groom Inver Grove"
                        >Zoom Groom Inver Grove
                        </a>
                    </li>
                </ul>
            </section>

            <section id="gallery" className="gallery">
                <h2 id="social-heading" className="cooper-outline">Recent Posts</h2>
                <InstagramFeed
                    instagramProxyServerApi={process.env.REACT_APP_INSTAGRAM_PROXY_SERVER_API}
                    startingCount={50}
                />
            </section>

            <section id="careers" className="careers">
                <h2 id="social-heading" className="cooper-outline">Improve your skills and work with the <b><i>BEST</i></b></h2>
                <div className="location-card">
                    <h3>We are hiring!</h3>

                    <h2 className="cooper-outline">Our Values</h2>
                    <ul>
                        <li><b>Professionalism:</b> We believe in behaving and speaking in a professional manner both in public and in private. <br/>
                            We believe dog grooming can be an elevated and respected profession <i>if we choose to make it so. </i> This requires us to actively participate in lifting up our profession.</li><br/>
                        <li><b>Hard work:</b> We believe in merit based outcomes. People who put in the hard work and live virtuously should benefit from their effort. </li>
                        <li><b>Teamwork:</b> We sharpen each other's skills and lift each other up. We are <i>honest</i> with one another when we need our friends and teammates to step up their game.</li>
                        <li><b>Communication:</b> We value and emphasize resolving problems early on and discussing issues openly and respectfully. We share and explain the reasoning behind our decisions and policies.</li>
                        <li><b>Respect:</b> We treat <b><i>everyone</i></b> with the same high level of respect. From Janitors to the CEOs. Everyone deserves our best.</li>
                    </ul>
                    <br/><br/>
                    <p>If you share our values, please send your resume and portfolio photos (or a link to your professional Instagram) to:</p>
                    <p>
                        <a href="mailto:doodlequeenllc@gmail.com"><b>doodlequeenllc@gmail.com</b></a>
                    </p>
                </div>
            </section>

            <div className="footer">
                <p>&copy; 2025 Zoom Groom. All rights reserved.</p>
            </div>
        </>
    );
}
